import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Dialog, EmptyState, Field, Input, Select, Spinner } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, newIdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { minorToInput, toMinor, useMoney } from '../lib/money';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  PriceList,
  ProductBulkOperation,
  ProductCellsPreview,
  ProductGrid,
  Warehouse,
} from '../lib/types';

/**
 * Tabloda düzenleme (F5B-09): varyant satırları, Excel benzeri hücre düzenleme ve klavyeyle gezinme (ARIA grid).
 * Değişiklikler yerelde tutulur; "Değişiklikleri kaydet" önizleme gösterir (yüklemeden beri değişen hücreler
 * çakışma olarak ayrılır, geçersiz hücreler nedeniyle), onaylanınca toplu işlem olarak uygulanır ve geri alınabilir.
 */

type GridField = 'sku' | 'barcode' | 'status' | 'priceMinor' | 'onHand';
type Row = ProductGrid['rows'][number];
type Cells = Partial<Record<GridField, unknown>>;
const FIELDS: GridField[] = ['sku', 'barcode', 'status', 'priceMinor', 'onHand'];
const key = (id: string, f: GridField) => `${id}:${f}`;

export function ProductGridPage() {
  const t = useT();
  const money = useMoney();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canPrice = can('pricing.price.read');
  const canStock = can('inventory.stock.read');
  const canApply = can('catalog.bulk.apply');
  const writable: Record<GridField, boolean> = {
    sku: canApply && can('catalog.product.write'),
    barcode: canApply && can('catalog.product.write'),
    status: canApply && can('catalog.product.write'),
    priceMinor: canApply && can('pricing.price.write'),
    onHand: canApply && can('inventory.stock.write'),
  };

  const lists = useQuery({
    queryKey: ['price-lists'],
    queryFn: () => api.get<PriceList[]>('/v1/admin/price-lists'),
    enabled: canPrice,
  });
  const whs = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api.get<Warehouse[]>('/v1/admin/warehouses'),
    enabled: canStock,
  });
  const [q, setQ] = useState('');
  const [listChoice, setListChoice] = useState<string | null>(null);
  const [whChoice, setWhChoice] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  // Varsayılan: ilk temel (grupsuz) fiyat listesi ve varsayılan depo.
  const priceListId =
    listChoice ?? lists.data?.find((l) => !l.customerGroupId && l.status === 'active')?.id ?? '';
  const warehouseId =
    whChoice ??
    whs.data?.find((w) => w.isDefault && w.status === 'active')?.id ??
    whs.data?.find((w) => w.status === 'active')?.id ??
    '';
  useEffect(() => setOffset(0), [q]);

  const params = new URLSearchParams({ offset: String(offset), limit: '50' });
  if (q.trim()) params.set('q', q.trim());
  if (priceListId) params.set('priceListId', priceListId);
  if (warehouseId) params.set('warehouseId', warehouseId);
  const ready = (!canPrice || lists.isFetched) && (!canStock || whs.isFetched);
  const grid = useQuery({
    queryKey: ['product-grid', params.toString()],
    queryFn: () => api.get<ProductGrid>(`/v1/admin/products/grid?${params.toString()}`),
    enabled: ready,
    refetchOnWindowFocus: false,
  });
  const currency = grid.data?.priceList?.currency ?? null;
  const cols = FIELDS.filter(
    (f) => (f !== 'priceMinor' || !!grid.data?.priceList) && (f !== 'onHand' || !!grid.data?.warehouse),
  );
  const rows = grid.data?.rows ?? [];

  // Yerel değişiklikler: yeni değer ve kullanıcının düzenlemeye başladığında gördüğü değer.
  const [edits, setEdits] = useState<Record<string, Cells>>({});
  const [seen, setSeen] = useState<Record<string, Cells>>({});
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [problems, setProblems] = useState<Record<string, string>>({});
  const dirty = Object.values(edits).reduce((a, c) => a + Object.keys(c).length, 0);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const valueOf = (r: Row, f: GridField): unknown => {
    const e = edits[r.variantId];
    return e && f in e ? e[f] : r[f];
  };
  const display = (r: Row, f: GridField): string => {
    const v = valueOf(r, f);
    if (f === 'status') return t(`admin.productGrid.statuses.${String(v)}`);
    if (f === 'priceMinor') return v === null ? '—' : money(v as number, currency);
    if (f === 'onHand') return r.lotTracked ? '—' : String(v);
    return (v as string | null) || '—';
  };
  const editable = (r: Row, f: GridField) =>
    writable[f] && !(f === 'onHand' && (r.lotTracked || !grid.data?.warehouse?.active));
  const rowLabel = (r: Row) => `${r.productTitle}${r.variantTitle ? ` — ${r.variantTitle}` : ''}`;

  const setCell = (r: Row, f: GridField, value: unknown) => {
    const original = f in (seen[r.variantId] ?? {}) ? seen[r.variantId]![f] : r[f];
    setProblems((p) => {
      const n = { ...p };
      delete n[key(r.variantId, f)];
      return n;
    });
    setEdits((cur) => {
      const row = { ...(cur[r.variantId] ?? {}) };
      if (JSON.stringify(original) === JSON.stringify(value)) delete row[f];
      else row[f] = value;
      const next = { ...cur, [r.variantId]: row };
      if (!Object.keys(row).length) delete next[r.variantId];
      return next;
    });
    setSeen((cur) =>
      f in (cur[r.variantId] ?? {})
        ? cur
        : { ...cur, [r.variantId]: { ...(cur[r.variantId] ?? {}), [f]: r[f] } },
    );
    setLabels((cur) => ({ ...cur, [r.variantId]: `${r.productTitle} — ${r.sku}` }));
  };
  const discard = () => {
    setEdits({});
    setSeen({});
    setProblems({});
  };

  // ---------------------------------------------------------------- klavye ve odak (ARIA grid)
  const [active, setActive] = useState({ r: 0, c: 0 });
  const [editing, setEditing] = useState<{ r: number; c: number; draft: string; error?: string } | null>(
    null,
  );
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const cellRefs = useRef(new Map<string, HTMLElement>());
  const lastCol = cols.length; // 0: ürün sütunu
  const focusCell = (r: number, c: number) => {
    const rr = Math.max(0, Math.min(rows.length - 1, r));
    const cc = Math.max(0, Math.min(lastCol, c));
    setActive({ r: rr, c: cc });
    cellRefs.current.get(`${rr}:${cc}`)?.focus();
  };
  const draftOf = (r: Row, f: GridField) => {
    const v = valueOf(r, f);
    if (f === 'priceMinor') return v === null ? '' : minorToInput(v as number);
    if (f === 'onHand' || f === 'status') return String(v);
    return (v as string | null) ?? '';
  };
  const startEdit = (r: number, c: number, initial?: string) => {
    const row = rows[r];
    const f = cols[c - 1];
    if (!row || !f || !editable(row, f)) return;
    setEditing({ r, c, draft: initial ?? draftOf(row, f) });
  };
  /** Düzenlenen hücreyi kaydeder; değer geçersizse düzenleme açık kalır. */
  const commit = (): boolean => {
    const ed = editingRef.current;
    if (!ed) return true;
    const row = rows[ed.r];
    const f = cols[ed.c - 1];
    if (!row || !f) return true;
    const raw = ed.draft.trim();
    let value: unknown = raw;
    if (f === 'priceMinor') {
      const m = currency ? toMinor(raw, currency) : null;
      if (m === null || m < 0) {
        setEditing({ ...ed, error: t('admin.productGrid.invalidPrice') });
        return false;
      }
      value = m;
    } else if (f === 'onHand') {
      if (!/^\d+$/.test(raw)) {
        setEditing({ ...ed, error: t('admin.productGrid.invalidStock') });
        return false;
      }
      value = Number(raw);
    } else if (f === 'barcode') value = raw || null;
    setCell(row, f, value);
    editingRef.current = null;
    setEditing(null);
    return true;
  };
  const cancel = () => {
    const ed = editingRef.current;
    editingRef.current = null;
    setEditing(null);
    if (ed) cellRefs.current.get(`${ed.r}:${ed.c}`)?.focus();
  };
  const onEditorKey = (e: KeyboardEvent) => {
    const ed = editingRef.current;
    if (!ed) return;
    // Düzenleyicinin tuşları tabloya kabarmaz (aksi hâlde Enter düzenlemeyi yeniden açar).
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === 'Tab') e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Odak hemen taşınır: hızlı yazılan sonraki tuş boşa düşmesin.
      if (commit()) focusCell(ed.r + 1, ed.c);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (commit()) focusCell(ed.r, ed.c + (e.shiftKey ? -1 : 1));
    }
  };
  const onGridKey = (e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    if (editingRef.current || tag === 'INPUT' || tag === 'SELECT') return;
    const { r, c } = active;
    const moves: Record<string, [number, number]> = {
      ArrowDown: [r + 1, c],
      ArrowUp: [r - 1, c],
      ArrowRight: [r, c + 1],
      ArrowLeft: [r, c - 1],
      PageDown: [r + 10, c],
      PageUp: [r - 10, c],
      Home: e.ctrlKey ? [0, 0] : [r, 0],
      End: e.ctrlKey ? [rows.length - 1, lastCol] : [r, lastCol],
    };
    const m = moves[e.key];
    if (m) {
      e.preventDefault();
      focusCell(m[0], m[1]);
    } else if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      startEdit(r, c);
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && cols[c - 1] !== 'status') {
      e.preventDefault();
      startEdit(r, c, e.key);
    }
  };
  useEffect(() => {
    setActive({ r: 0, c: 0 });
    setEditing(null);
  }, [grid.data]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const header = (f: GridField) =>
    f === 'priceMinor'
      ? t('admin.productGrid.cols.priceIn', { currency: currency ?? '' })
      : t(`admin.productGrid.cols.${f}`);

  return (
    <PageFrame helpKey="page.product_grid" wide>
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('admin.products.search')} className="w-72">
          {(p) => (
            <Input
              {...p}
              type="search"
              value={q}
              placeholder={t('admin.products.searchPlaceholder')}
              onChange={(e) => setQ(e.target.value)}
            />
          )}
        </Field>
        {canPrice && (
          <Field
            label={t('admin.productGrid.priceList')}
            help={<HelpTip k="products.grid_price_list" />}
            className="w-60"
          >
            {(p) => (
              <Select
                {...p}
                value={priceListId}
                disabled={dirty > 0}
                onChange={(e) => {
                  setListChoice(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="">{t('admin.productGrid.noPriceList')}</option>
                {(lists.data ?? []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.currency})
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        {canStock && (
          <Field
            label={t('admin.productGrid.warehouse')}
            help={<HelpTip k="products.grid_warehouse" />}
            className="w-52"
          >
            {(p) => (
              <Select
                {...p}
                value={warehouseId}
                disabled={dirty > 0}
                onChange={(e) => {
                  setWhChoice(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="">{t('admin.productGrid.noWarehouse')}</option>
                {(whs.data ?? []).map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
      </div>
      {dirty > 0 && (canPrice || canStock) && (
        <p className="-mt-5 text-sm text-ink-3">{t('admin.productGrid.lockedWhileDirty')}</p>
      )}

      <div
        role="region"
        aria-label={t('admin.productGrid.changesRegion')}
        className="zt-sticky-top sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-md border border-line bg-surface px-4 py-2 text-sm"
      >
        <span className="font-medium" aria-live="polite">
          {dirty ? t('admin.productGrid.changes', { count: dirty }) : t('admin.productGrid.noChanges')}
        </span>
        <span className="ms-auto inline-flex items-center gap-1">
          <ConfirmButton
            variant="ghost"
            disabled={!dirty}
            message={t('admin.productGrid.discardConfirm', { count: dirty })}
            onConfirm={discard}
          >
            {t('admin.productGrid.discard')}
          </ConfirmButton>
          <HelpTip k="products.grid_discard" />
          <Button size="sm" variant="primary" disabled={!dirty} onClick={() => setPreviewOpen(true)}>
            {t('admin.productGrid.review')}
          </Button>
          <HelpTip k="products.grid_save" />
        </span>
      </div>
      <p id="grid-keys" className="text-sm text-ink-3">
        {t('admin.productGrid.keyboard')}
      </p>

      {!grid.data ? (
        <Spinner label={t('common.loading')} />
      ) : !rows.length ? (
        <EmptyState title={t('admin.productGrid.empty')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table
            role="grid"
            aria-label={t('admin.productGrid.tableLabel')}
            aria-describedby="grid-keys"
            className="w-full min-w-[56rem] border-collapse text-sm"
            onKeyDown={onGridKey}
          >
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.productGrid.cols.product')}</th>
                {cols.map((f) => (
                  <th
                    key={f}
                    className={`px-3 py-2 font-medium ${f === 'priceMinor' || f === 'onHand' ? 'text-end' : 'text-start'}`}
                  >
                    {header(f)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={r.variantId} className="border-t border-line">
                  <th
                    scope="row"
                    ref={(el) => {
                      if (el) cellRefs.current.set(`${ri}:0`, el);
                    }}
                    tabIndex={active.r === ri && active.c === 0 ? 0 : -1}
                    onFocus={() => setActive({ r: ri, c: 0 })}
                    className="px-3 py-1.5 text-start font-normal focus-visible:outline-offset-[-2px]"
                  >
                    <span className="font-medium">{r.productTitle}</span>
                    {r.variantTitle && <span className="block text-ink-3">{r.variantTitle}</span>}
                  </th>
                  {cols.map((f, i) => {
                    const ci = i + 1;
                    const isEditing = editing?.r === ri && editing.c === ci;
                    const changed = !!edits[r.variantId] && f in edits[r.variantId]!;
                    const problem = problems[key(r.variantId, f)];
                    const canEdit = editable(r, f);
                    const label = `${rowLabel(r)} — ${header(f)}`;
                    return (
                      <td
                        key={f}
                        role="gridcell"
                        ref={(el) => {
                          if (el) cellRefs.current.set(`${ri}:${ci}`, el);
                        }}
                        tabIndex={isEditing ? -1 : active.r === ri && active.c === ci ? 0 : -1}
                        aria-readonly={!canEdit || undefined}
                        aria-invalid={problem ? true : undefined}
                        title={
                          problem
                            ? t(`errors.${problem}`)
                            : changed
                              ? t('admin.productGrid.previously', {
                                  value: displaySeen(seen[r.variantId]?.[f], f, t, money, currency),
                                })
                              : !canEdit && f === 'onHand' && r.lotTracked
                                ? t('admin.productGrid.lotTracked')
                                : undefined
                        }
                        onFocus={() => setActive({ r: ri, c: ci })}
                        onDoubleClick={() => startEdit(ri, ci)}
                        className={[
                          'px-3 py-1.5 focus-visible:outline-offset-[-2px]',
                          f === 'priceMinor' || f === 'onHand' ? 'text-end tabular' : '',
                          f === 'sku' || f === 'barcode' ? 'font-mono text-[0.8125rem]' : '',
                          changed ? 'bg-amber-tint' : '',
                          problem ? 'bg-rose-tint text-rose' : '',
                          !canEdit ? 'text-ink-3' : '',
                        ].join(' ')}
                      >
                        {isEditing ? (
                          <span className="block">
                            {f === 'status' ? (
                              <select
                                autoFocus
                                aria-label={label}
                                className="w-full rounded-sm border border-pine bg-surface px-1 py-0.5"
                                value={editing.draft}
                                onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
                                onKeyDown={onEditorKey}
                                onBlur={() => commit()}
                              >
                                {['active', 'archived'].map((s) => (
                                  <option key={s} value={s}>
                                    {t(`admin.productGrid.statuses.${s}`)}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                autoFocus
                                aria-label={label}
                                aria-invalid={editing.error ? true : undefined}
                                aria-describedby={editing.error ? 'grid-edit-error' : undefined}
                                inputMode={
                                  f === 'onHand' ? 'numeric' : f === 'priceMinor' ? 'decimal' : undefined
                                }
                                className={`w-full rounded-sm border bg-surface px-1 py-0.5 ${editing.error ? 'border-rose' : 'border-pine'} ${f === 'priceMinor' || f === 'onHand' ? 'text-end' : ''}`}
                                value={editing.draft}
                                onChange={(e) =>
                                  setEditing({ ...editing, draft: e.target.value, error: undefined })
                                }
                                onKeyDown={onEditorKey}
                                onBlur={() => commit()}
                              />
                            )}
                            {editing.error && (
                              <span
                                id="grid-edit-error"
                                role="alert"
                                className="mt-0.5 block text-xs text-rose"
                              >
                                {editing.error}
                              </span>
                            )}
                          </span>
                        ) : (
                          <>
                            {display(r, f)}
                            {changed && <span className="sr-only"> ({t('admin.productGrid.edited')})</span>}
                            {problem && <span className="sr-only"> ({t(`errors.${problem}`)})</span>}
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {grid.data && (offset > 0 || grid.data.nextOffset !== null) && (
        <div className="flex gap-2">
          <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>
            {t('admin.productGrid.prevPage')}
          </Button>
          <Button disabled={grid.data.nextOffset === null} onClick={() => setOffset(grid.data!.nextOffset!)}>
            {t('admin.productGrid.nextPage')}
          </Button>
        </div>
      )}
      {previewOpen && (
        <SaveDialog
          priceListId={grid.data?.priceList?.id ?? null}
          warehouseId={grid.data?.warehouse?.id ?? null}
          currency={currency}
          edits={edits}
          seen={seen}
          labels={labels}
          onClose={() => setPreviewOpen(false)}
          onSaved={(failed) => {
            // Kaydedilenler yerelden silinir; kaydedilemeyen hücreler işaretli kalır.
            setEdits((cur) => {
              const next: Record<string, Cells> = {};
              for (const [id, c] of Object.entries(cur))
                for (const [f, v] of Object.entries(c))
                  if (failed[key(id, f as GridField)]) next[id] = { ...(next[id] ?? {}), [f]: v };
              return next;
            });
            setSeen((cur) => {
              const next: Record<string, Cells> = {};
              for (const [id, c] of Object.entries(cur))
                for (const [f, v] of Object.entries(c))
                  if (failed[key(id, f as GridField)]) next[id] = { ...(next[id] ?? {}), [f]: v };
              return next;
            });
            setProblems(failed);
            void qc.invalidateQueries({ queryKey: ['product-grid'] });
            void qc.invalidateQueries({ queryKey: ['products'] });
          }}
        />
      )}
    </PageFrame>
  );
}

function displaySeen(
  v: unknown,
  f: GridField,
  t: (k: string, p?: Record<string, string | number>) => string,
  money: (m: number | null, c: string | null) => string,
  currency: string | null,
): string {
  if (v === undefined || v === null || v === '') return '—';
  if (f === 'status') return t(`admin.productGrid.statuses.${String(v)}`);
  if (f === 'priceMinor') return money(v as number, currency);
  return String(v);
}

/** Kaydetme penceresi: önizleme (değişiklikler, çakışmalar, geçersiz hücreler) → Kaydet. */
function SaveDialog({
  priceListId,
  warehouseId,
  currency,
  edits,
  seen,
  labels,
  onClose,
  onSaved,
}: {
  priceListId: string | null;
  warehouseId: string | null;
  currency: string | null;
  edits: Record<string, Cells>;
  seen: Record<string, Cells>;
  labels: Record<string, string>;
  onClose: () => void;
  onSaved: (failed: Record<string, string>) => void;
}) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const fieldName = (f: string) =>
    f === 'priceMinor' ? t('admin.productGrid.cols.price') : t(`admin.productGrid.cols.${f}`);
  const show = (f: string, v: unknown) => displaySeen(v, f as GridField, t, money, currency);
  const [preview, setPreview] = useState<ProductCellsPreview | null>(null);
  const [queued, setQueued] = useState(false);
  const body = useMemo(
    () => ({
      priceListId,
      warehouseId,
      rows: Object.entries(edits).map(([variantId, after]) => ({
        variantId,
        before: Object.fromEntries(
          Object.keys(after).map((f) => [f, seen[variantId]?.[f as GridField] ?? null]),
        ),
        after,
      })),
    }),
    [priceListId, warehouseId, edits, seen],
  );
  const run = useApiMutation(
    () => api.post<ProductCellsPreview>('/v1/admin/products/bulk/cells/preview', body),
    { onSuccess: setPreview },
  );
  useEffect(() => {
    run.mutate(undefined);
    // Pencere açılınca bir kez önizlenir.
  }, []);
  const failed = useMemo(() => {
    const out: Record<string, string> = {};
    for (const p of preview?.problems ?? []) out[key(p.variantId, p.field)] = p.code;
    return out;
  }, [preview]);
  const apply = useApiMutation(
    (id: string) =>
      api.post<ProductBulkOperation>(`/v1/admin/products/bulk/${id}/apply`, undefined, {
        idempotencyKey: newIdempotencyKey(),
      }),
    {
      onSuccess: (r) => {
        onSaved(failed);
        if (r.status === 'queued') setQueued(true);
        else onClose();
      },
      success: (r) =>
        r.status === 'queued'
          ? t('admin.productGrid.queued')
          : t('admin.productGrid.saved', { count: preview?.total ?? 0 }),
    },
  );
  const lines = (preview?.sample ?? []).flatMap((s) =>
    Object.keys(s.after as object).map((f) => ({
      key: `${s.variantId}:${f}`,
      label: s.label,
      field: f,
      before: (s.before as Cells)[f as GridField],
      after: (s.after as Cells)[f as GridField],
    })),
  );
  return (
    <Dialog
      open
      variant="sheet"
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.productGrid.previewTitle')}
      description={t('admin.productGrid.previewLead')}
      closeLabel={t('common.close')}
      footer={
        queued ? (
          <Button variant="primary" onClick={onClose}>
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>{t('admin.productBulk.back')}</Button>
            <Button
              variant="primary"
              disabled={!preview?.total}
              loading={apply.isPending}
              onClick={() => preview && apply.mutate(preview.id)}
            >
              {t('admin.productGrid.save')}
            </Button>
          </>
        )
      }
    >
      {queued ? (
        <p role="status" className="text-sm">
          {t('admin.productGrid.queued')}
        </p>
      ) : !preview ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-4 text-sm">
          <p role="status" className="font-medium">
            {preview.total
              ? t('admin.productGrid.summary', { total: preview.total })
              : t('admin.productGrid.nothing')}
          </p>
          {lines.length > 0 && (
            <div className="max-h-[20rem] overflow-auto rounded-md border border-line">
              <table className="w-full text-sm">
                <caption className="sr-only">{t('admin.productGrid.previewTitle')}</caption>
                <thead className="sticky top-0 bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-1.5 text-start font-medium">{t('admin.productBulk.item')}</th>
                    <th className="px-3 py-1.5 text-start font-medium">{t('admin.productGrid.field')}</th>
                    <th className="px-3 py-1.5 text-start font-medium">{t('admin.productBulk.before')}</th>
                    <th className="px-3 py-1.5 text-start font-medium">{t('admin.productBulk.after')}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.key} className="border-t border-line align-top">
                      <td className="px-3 py-1.5">{l.label}</td>
                      <td className="px-3 py-1.5">{fieldName(l.field)}</td>
                      <td className="px-3 py-1.5 text-ink-3">{show(l.field, l.before)}</td>
                      <td className="px-3 py-1.5 font-medium">{show(l.field, l.after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.total > preview.sample.length && (
            <p className="text-ink-3">{t('admin.productBulk.sampleNote')}</p>
          )}
          {preview.conflicts.length > 0 && (
            <section aria-labelledby="grid-conflicts">
              <h3 id="grid-conflicts" className="font-medium">
                {t('admin.productGrid.conflictsTitle', { count: preview.conflicts.length })}
              </h3>
              <p className="mb-1 text-ink-3">{t('admin.productGrid.conflictsLead')}</p>
              <ul className="space-y-0.5">
                {preview.conflicts.map((c) => (
                  <li key={`${c.variantId}:${c.field}`}>
                    <Badge tone="amber">{fieldName(c.field)}</Badge> {c.label}:{' '}
                    {t('admin.productGrid.conflict', {
                      yours: show(c.field, c.yours),
                      current: show(c.field, c.current),
                    })}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {preview.problems.length > 0 && (
            <section aria-labelledby="grid-problems">
              <h3 id="grid-problems" className="font-medium">
                {t('admin.productGrid.problemsTitle', { count: preview.problems.length })}
              </h3>
              <ul className="space-y-0.5">
                {preview.problems.map((p) => (
                  <li key={`${p.variantId}:${p.field}`}>
                    <Badge tone="rose">{fieldName(p.field)}</Badge> {labels[p.variantId] ?? p.label}:{' '}
                    {t(`errors.${p.code}`)}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <p className="text-ink-3">
            {t('admin.productBulk.expires', { time: formatDate(preview.expiresAt, true) })}
          </p>
        </div>
      )}
    </Dialog>
  );
}
